/**
 * PRIVACY: Geen user-input naar server, localStorage (behalve onboarding-flag),
 * externe APIs. PortalAuth verify via SDK; minimale Umami analytics (tool_open/close).
 */
import { useCallback, useEffect, useState } from "react";
import {
  ToolLayout,
  ToolFileActions,
  PaywallOverlay,
  Onboarding,
  useAccess,
  hasCompletedOnboarding,
  setOnboardingComplete,
  downloadJariFile,
  type OnboardingSlide,
} from "@jari-tools/shared";
import type { DriehoekData } from "./types/driehoek-data";
import {
  getDefaultData,
  addItem,
  addTopSectionItem,
  updateItem,
  deleteItem,
  updateTopSectionItem,
  deleteTopSectionItem,
  clearAllData,
  parseDriehoekFile,
  getDebugFixtureData,
} from "./lib/data-ops";
import { ItemLists } from "./components/ItemLists";
import { SidebarSections } from "./components/SidebarSections";
import { TriangleCanvas } from "./components/TriangleCanvas";

const TOOL_SLUG = import.meta.env.VITE_TOOL_SLUG || "context-driehoek";
const TOOL_NAME = "Driehoekmodel";
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL;

const ONBOARDING_SLIDES: OnboardingSlide[] = [
  {
    title: "Het driehoekmodel",
    body: "Het driehoekmodel helpt om factoren bij het kind, gezin, en de omgeving snel overzichtelijk te maken, te ordenen en verbanden zichtbaar te maken.",
  },
  {
    title: "Gedeeld beeld",
    body: "Door het invullen van dit model ontstaat een gedeeld beeld van de casus en wordt het eenvoudiger om hypotheses te formuleren, ontbrekende informatie te signaleren en vervolgstappen te bepalen.",
  },
  {
    title: "Gratis en betaald",
    body: "Je kunt het model gratis invullen en gebruiken. Opslaan, openen en exporteren zit in de betaalde versie.",
  },
];

const PRINT_TRIANGLE_MAX_CM = 20;
const CM_TO_PX = 96 / 2.54;

function measurePrintTriangleSizePx(): number {
  const el = document.querySelector(".cd-triangle-container");
  const maxPx = PRINT_TRIANGLE_MAX_CM * CM_TO_PX;
  if (!el) return maxPx;
  return Math.min(el.getBoundingClientRect().width, maxPx);
}

export function App() {
  const { access, name, loading, error, errorCode, isPaid, isGuest, toolId } = useAccess();
  const [data, setData] = useState<DriehoekData>(getDefaultData);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !hasCompletedOnboarding(TOOL_SLUG)
  );
  const [printMode, setPrintMode] = useState(false);
  const [showLabelDebug, setShowLabelDebug] = useState(false);

  const loadDebugFixture = useCallback(() => {
    const fixture = getDebugFixtureData();
    if (fixture) setData(fixture);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setShowLabelDebug((on) => {
          const next = !on;
          if (next) loadDebugFixture();
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [loadDebugFixture]);

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(TOOL_SLUG);
    setShowOnboarding(false);
  }, []);

  const handleSave = () => {
    downloadJariFile(TOOL_SLUG, data, "context-driehoek");
  };

  const handleOpen = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseDriehoekFile(String(reader.result));
      if (!parsed) {
        window.alert("Ongeldig .jari-bestand.");
        return;
      }
      setData(parsed);
    };
    reader.readAsText(file);
  };

  const handlePrint = () => {
    const root = document.documentElement;
    const sizePx = measurePrintTriangleSizePx();
    root.style.setProperty("--cd-print-triangle-size", `${sizePx}px`);

    const endPrint = () => {
      setPrintMode(false);
      root.style.removeProperty("--cd-print-triangle-size");
      window.removeEventListener("afterprint", endPrint);
    };
    window.addEventListener("afterprint", endPrint);
    setPrintMode(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print());
    });
  };

  const handleNew = () => {
    if (window.confirm("Nieuw bestand starten? Niet-opgeslagen wijzigingen gaan verloren.")) {
      setData(getDefaultData());
    }
  };

  return (
    <>
      <ToolLayout
        toolName={TOOL_NAME}
        access={access}
        userName={name}
        loading={loading}
        error={error}
        errorCode={errorCode}
        portalUrl={PORTAL_URL}
        isGuest={isGuest}
        toolId={toolId}
        toolSlug={TOOL_SLUG}
        onShowOnboarding={() => setShowOnboarding(true)}
        fileActions={
          <div className="cd-header-actions">
            <ToolFileActions
              locked={!isPaid}
              featureTitle="opslaan en openen"
              portalUrl={PORTAL_URL}
              saveLabel="Opslaan (.jari)"
              onSave={handleSave}
              onOpen={handleOpen}
              onNew={handleNew}
            />
            <PaywallOverlay
              locked={!isPaid}
              featureTitle="printen naar PDF"
              portalUrl={PORTAL_URL}
            >
              <button type="button" className="jari-btn" onClick={handlePrint}>
                Exporteer PDF
              </button>
            </PaywallOverlay>
          </div>
        }
      >
        <div className={`cd-workspace${printMode ? " cd-print-active" : ""}`}>
          <ItemLists
            data={data}
            onBasisgegevensChange={(basisgegevens) => setData({ ...data, basisgegevens })}
            onAddFactor={(factor, text) => setData(addItem(data, factor, text))}
            onAddSection={(section, text) => setData(addTopSectionItem(data, section, text))}
            onEditFactor={(id, text) => setData(updateItem(data, id, { text }))}
            onDeleteFactor={(id) => setData(deleteItem(data, id))}
            onEditSection={(id, text) => setData(updateTopSectionItem(data, id, { text }))}
            onDeleteSection={(id) => setData(deleteTopSectionItem(data, id))}
            onClear={() => {
              if (window.confirm("Weet je zeker dat je alle data wilt wissen?")) {
                setData(clearAllData(data));
              }
            }}
          />

          <div className="cd-content-area">
            <div className="cd-content-left">
              <TriangleCanvas
                data={data}
                onItemsChange={(items) => setData({ ...data, items })}
                printMode={printMode}
                showLabelDebug={showLabelDebug}
                onLoadDebugFixture={loadDebugFixture}
              />
            </div>
            <SidebarSections data={data} forceOpen={printMode} />
          </div>

        </div>
      </ToolLayout>

      <Onboarding
        open={showOnboarding}
        slides={ONBOARDING_SLIDES}
        onComplete={completeOnboarding}
        onSkip={completeOnboarding}
      />
    </>
  );
}
